import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ClipboardCopy,
  Download,
  FolderOpen,
  Save,
  Eye,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { FormSkeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { useGpnsVlans } from "@/stores/gpns-vlans";
import type { GPNSVlan } from "@/api/schemas/gpns-vlans";
import { renderFirewallConfig, renderRoutableVlansConfig, renderVendorVlansConfig } from "@/lib/firewall-config";
import type { TemplateVlan, FirewallConfigVars } from "@/lib/firewall-config";

// ─── Startup template (mirrors startup.j2) ──────────────────────────────────

const STARTUP_TEMPLATE = `\
# ============================================
# STARTUP
# ============================================

root
cli
edit

delete security
delete system
delete protocols
delete interfaces
delete access
delete vlans
delete protocols
delete forwarding-options helpers bootp

set system login user admin uid 2002 class super-user authentication plain-text-password

1ns2deout
1ns2deout

set system root-authentication plain-text-password

1ns2deout
1ns2deout

set security zones security-zone GTK_MGMT
set interfaces ge-0/0/1 unit 0 description GTK_MGMT
set interfaces ge-0/0/1 unit 0 family inet address {{solve_ip_equation("w.x.y+3.254", HOTEL_SUBNET)}}/25
set security zones security-zone GTK_MGMT interfaces ge-0/0/1.0
set security zones security-zone GTK_MGMT host-inbound-traffic system-services all

set system services ssh
set system services netconf ssh
set firewall family inet filter GTK_MGMT_Filter term Allow_SSH from source-address {{solve_ip_equation("w.x.y+3.129", HOTEL_SUBNET)}}/32 except
set firewall family inet filter GTK_MGMT_Filter term Allow_SSH from protocol tcp
set firewall family inet filter GTK_MGMT_Filter term Allow_SSH from destination-port ssh
set firewall family inet filter GTK_MGMT_Filter term Allow_SSH then discard
set applications application-set ObjGrp_SixCard_Ports application junos-ssh

commit
`;

/**
 * Port of the Python solve_ip_equation utility.
 * Replaces octet variables (w/x/y/z) in an equation string with the
 * corresponding octets of baseIp, supporting +/- arithmetic per octet.
 *
 * e.g. solveIpEquation("w.x.y+3.254", "10.20.30.0") → "10.20.33.254"
 */
function solveIpEquation(equation: string, baseIp: string): string {
  const octets = baseIp.split(".");
  const [w, x, y, z] = octets.map(Number);
  const vars: Record<string, number> = { w, x, y, z };

  return equation
    .split(".")
    .map((part) => {
      // Plain number — use as-is.
      if (/^\d+$/.test(part)) return part;

      // Variable with optional +/- constant: e.g. "y+3", "z-1", "w"
      const m = part.match(/^([wxyz])([+-]\d+)?$/);
      if (!m) return part; // unknown token — leave unchanged
      const base = vars[m[1]] ?? 0;
      const delta = m[2] ? parseInt(m[2], 10) : 0;
      return String(Math.min(255, Math.max(0, base + delta)));
    })
    .join(".");
}

/**
 * Render the startup Jinja2 template in the browser.
 * Resolves {{solve_ip_equation("eq", VAR)}} calls and substitutes
 * plain {{VAR}} tokens using the provided variables map.
 */
function renderStartupTemplate(
  template: string,
  vars: Record<string, string>,
): string {
  // 1. Resolve {{solve_ip_equation("equation", VAR_NAME)}} calls.
  let out = template.replace(
    /\{\{solve_ip_equation\("([^"]+)",\s*(\w+)\)\}\}/g,
    (_match, equation: string, varName: string) => {
      const baseIp = vars[varName];
      if (!baseIp || !isValidIpv4(baseIp)) return `<${varName} not set>`;
      try {
        return solveIpEquation(equation, baseIp);
      } catch {
        return `<error: ${equation}>`;
      }
    },
  );

  // 2. Substitute plain {{VAR}} tokens.
  out = out.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => {
    return vars[key] ?? `<${key}>`;
  });

  return out;
}

// ─── IP post-processing (mirrors solveIp.ts) ────────────────────────────────

function cidrAssign(cidr: string): string {
  const map: Record<string, string> = {
    "32": "w.x.y.z", "31": "w.x.y.z", "30": "w.x.y.z+2", "29": "w.x.y.z+6",
    "28": "w.x.y.z+14", "27": "w.x.y.z+30", "26": "w.x.y.z+62", "25": "w.x.y.z+126",
    "24": "w.x.y.z+254", "23": "w.x.y+1.z+254", "22": "w.x.y+3.z+254",
    "21": "w.x.y+7.z+254", "20": "w.x.y+15.z+254", "19": "w.x.y+31.z+254",
  };
  return map[cidr] ?? "";
}

function getEndOfRange(ip: string, number: string, cidr: string): string {
  if (number === "841") return solveIpEquation("w.x.y.200", ip);
  const formula = cidrAssign(cidr);
  return formula ? solveIpEquation(formula, ip) : ip;
}

interface ProcessedVlan extends GPNSVlan {
  selected: boolean;
  resolvedNetworkAddress: string;
  resolvedIp: string;
  resolvedDhcpLow: string;
  resolvedDhcpHigh: string;
  resolvedDhcpRouter: string;
}

function postProcessVlans(vlans: GPNSVlan[], hotelSubnet: string, defaultSelected = true): ProcessedVlan[] {
  return vlans.map((vlan) => {
    const networkAddress = vlan.routable && vlan.networkAddress
      ? solveIpEquation(vlan.networkAddress, hotelSubnet)
      : (vlan.networkAddress ?? "");
    const resolvedIp = vlan.ip ? solveIpEquation(vlan.ip, networkAddress) : "";
    const resolvedDhcpLow = vlan.dhcpLow ? solveIpEquation(vlan.dhcpLow, networkAddress) : "";
    const resolvedDhcpHigh = getEndOfRange(networkAddress, vlan.number, vlan.cidr);
    const resolvedDhcpRouter = vlan.dhcpRouter ? solveIpEquation(vlan.dhcpRouter, networkAddress) : "";
    return {
      ...vlan,
      selected: defaultSelected,
      resolvedNetworkAddress: networkAddress,
      resolvedIp,
      resolvedDhcpLow,
      resolvedDhcpHigh,
      resolvedDhcpRouter,
    };
  });
}

// ─── Firewall config helpers ─────────────────────────────────────────────────

function toTemplateVlan(v: ProcessedVlan): TemplateVlan {
  return {
    number: v.number,
    name: v.name,
    nickname: v.nickname ?? "",
    ip: v.resolvedIp,
    cidr: v.cidr,
    network_address: v.resolvedNetworkAddress,
    dhcp: v.dhcp,
    dhcp_low: v.resolvedDhcpLow,
    dhcp_high: v.resolvedDhcpHigh,
    dhcp_router: v.resolvedDhcpRouter,
  };
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface GeneralConfig {
  vendor: string;
  marsha: string;
  public_ip: string;
  public_netmask: string;
  gateway: string;
  hotel_subnet: string;
  timezone: string;
}

interface VlanFilterConfig {
  network_mask: string;
  version: string;
  type: string;
}

interface AdditionalOptions {
  multi_circuit: boolean;
  clear_alerts: boolean;
}

interface SaveForm {
  name: string;
  description: string;
  system_preset: boolean;
  public_config: boolean;
}

// ─── Step indicator ──────────────────────────────────────────────────────────

const STEPS = [
  { n: 1, label: "General" },
  { n: 2, label: "Routable VLANs" },
  { n: 3, label: "Vendor VLANs" },
  { n: 4, label: "Additional Options" },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-0">
      {STEPS.map((s, i) => (
        <div key={s.n} className="flex items-center">
          <div className="flex flex-col items-center gap-1">
            <div
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full border-2 text-[12px] font-bold transition-colors",
                s.n === current
                  ? "border-violet bg-aurora-soft text-violet"
                  : s.n < current
                    ? "border-violet/50 bg-violet/10 text-violet/70"
                    : "border-line bg-surface text-fg-mute",
              )}
            >
              {s.n}
            </div>
            <span
              className={cn(
                "text-[10px] font-medium",
                s.n === current ? "text-violet" : "text-fg-mute",
              )}
            >
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div
              className={cn(
                "mb-4 h-[2px] w-16 transition-colors",
                s.n < current ? "bg-violet/40" : "bg-line",
              )}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Modal shell ────────────────────────────────────────────────────────────

function Modal({
  title,
  open,
  onClose,
  children,
  footer,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-bg-0/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 flex max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl border border-line bg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h2 className="text-base font-semibold text-fg">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-fg-mute hover:bg-surface-2 hover:text-fg"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-auto px-6 py-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-line px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Inline field ───────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label>{label}</Label>
      {children}
      {error && <span className="text-[11px] text-rose">{error}</span>}
      {!error && hint && <span className="text-[11px] text-fg-mute">{hint}</span>}
    </div>
  );
}

// ─── IP validation ───────────────────────────────────────────────────────────

function isValidIpv4(value: string): boolean {
  if (!value) return true; // empty = not yet filled, not an error
  const parts = value.split(".");
  if (parts.length !== 4) return false;
  return parts.every((p) => {
    if (p === "" || /[^0-9]/.test(p)) return false;
    const n = Number(p);
    return n >= 0 && n <= 255;
  });
}

function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
  const allowed = /^[0-9.]$/.test(e.key);
  const control =
    e.key === "Backspace" ||
    e.key === "Delete" ||
    e.key === "Tab" ||
    e.key === "ArrowLeft" ||
    e.key === "ArrowRight" ||
    e.metaKey ||
    e.ctrlKey;
  if (!allowed && !control) e.preventDefault();
}

/** Restricts keystrokes to digits and dots, and validates on blur. */
function IpInput({
  value,
  placeholder,
  onChange,
  onBlur,
}: {
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
}) {
  const invalid = value !== "" && !isValidIpv4(value);

  return (
    <Input
      value={value}
      placeholder={placeholder}
      maxLength={15}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={onBlur}
      className={cn(invalid && "border-rose/60 focus-visible:ring-rose/40")}
    />
  );
}

// ─── GPNS VLAN table ─────────────────────────────────────────────────────────

const VLAN_COLS: { key: keyof ProcessedVlan | string; label: string; editable?: boolean; checkbox?: boolean; width?: string }[] = [
  { key: "selected",              label: "✓",           checkbox: true,  width: "w-8"  },
  { key: "number",                label: "#",                            width: "w-12" },
  { key: "name",                  label: "Name",                         width: "w-40" },
  { key: "resolvedNetworkAddress",label: "Network Addr",                 width: "w-32" },
  { key: "cidr",                  label: "CIDR",                        width: "w-12" },
  { key: "dhcp",                  label: "DHCP",        checkbox: true,  width: "w-12" },
  { key: "resolvedDhcpLow",       label: "DHCP Low",    editable: true,  width: "w-28" },
  { key: "resolvedDhcpHigh",      label: "DHCP High",   editable: true,  width: "w-28" },
  { key: "resolvedDhcpRouter",    label: "DHCP Router", editable: true,  width: "w-28" },
  { key: "resolvedIp",            label: "IP",          editable: true,  width: "w-28" },
];

function RoutableVlanTable({
  vlans,
  onChange,
  scrollable = false,
}: {
  vlans: ProcessedVlan[];
  onChange: (vlans: ProcessedVlan[]) => void;
  scrollable?: boolean;
}) {
  function update(number: string, key: string, val: string | boolean) {
    onChange(vlans.map((v) => v.number === number ? { ...v, [key]: val } : v));
  }

  if (vlans.length === 0) {
    return (
      <div className="rounded-xl border border-line bg-surface p-6 text-center text-sm text-fg-mute">
        No VLANs match the selected filters.
      </div>
    );
  }

  return (
    <div className={cn("overflow-auto rounded-xl border border-line", scrollable && "max-h-[420px]")}>
      <table className="w-full min-w-[800px] text-[12px]">
        <thead>
          <tr className="border-b border-line bg-surface-2">
            {VLAN_COLS.map((c) => (
              <th key={c.key} className={cn("px-2 py-2 text-left font-semibold text-fg-mute", c.width)}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {vlans.map((vlan) => (
            <tr key={vlan.number} className="border-b border-line last:border-0 hover:bg-surface-2/50">
              {VLAN_COLS.map((c) => {
                const raw = (vlan as unknown as Record<string, unknown>)[c.key];
                if (c.checkbox) {
                  return (
                    <td key={c.key} className="px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        aria-label={c.label}
                        checked={!!raw}
                        onChange={(e) => update(vlan.number, c.key, e.target.checked)}
                        className="accent-violet"
                      />
                    </td>
                  );
                }
                if (c.editable) {
                  return (
                    <td key={c.key} className="px-1 py-1">
                      <input
                        aria-label={c.label}
                        value={String(raw ?? "")}
                        onChange={(e) => update(vlan.number, c.key, e.target.value)}
                        className="w-full rounded border border-transparent bg-transparent px-1.5 py-1 font-mono text-[11px] text-fg hover:border-line focus:border-violet/50 focus:outline-none focus:ring-1 focus:ring-violet/30"
                      />
                    </td>
                  );
                }
                return (
                  <td key={c.key} className="px-2 py-1 font-mono text-fg-dim">
                    {String(raw ?? "—")}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Australia/Sydney",
];

function copyText(text: string) {
  void navigator.clipboard?.writeText(text);
  toast.success("Copied to clipboard");
}

function downloadText(text: string, filename: string) {
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function useConfigGenerator() {
  const [step, setStep] = useState(1);
  const [publicIpTouched, setPublicIpTouched] = useState(false);
  const [netmaskTouched, setNetmaskTouched] = useState(false);
  const [gatewayTouched, setGatewayTouched] = useState(false);
  const [hotelSubnetTouched, setHotelSubnetTouched] = useState(false);

  // Step 1 — General
  const [general, setGeneral] = useState<GeneralConfig>({
    vendor: "juniper",
    marsha: "",
    public_ip: "",
    public_netmask: "",
    gateway: "",
    hotel_subnet: "",
    timezone: "America/New_York",
  });

  // GPNS VLANs from API
  const { data: allVlans, status: vlansStatus, load: loadVlans } = useGpnsVlans();

  // Step 2 — Routable VLANs
  const [routableFilter, setRoutableFilter] = useState<VlanFilterConfig>({
    network_mask: "",
    version: "",
    type: "",
  });
  const derivedRoutableVlans = useMemo(() => {
    if (allVlans.length === 0 || !routableFilter.network_mask) return [];
    const filtered = allVlans.filter((v) => {
      const versionKeys = v.version.map((ver) => `${ver.year}v${ver.version}`);
      const typeNames = v.type.map((t) => t.type);
      return (
        v.routable &&
        v.netmask.includes(routableFilter.network_mask) &&
        versionKeys.includes(routableFilter.version) &&
        typeNames.includes(routableFilter.type)
      );
    });
    const hotelSubnet = isValidIpv4(general.hotel_subnet) ? general.hotel_subnet : "10.0.0.0";
    return postProcessVlans(filtered, hotelSubnet);
  }, [allVlans, routableFilter, general.hotel_subnet]);

  const [routableVlans, setRoutableVlans] = useState(derivedRoutableVlans);
  const [prevDerivedRoutableVlans, setPrevDerivedRoutableVlans] = useState(derivedRoutableVlans);
  if (derivedRoutableVlans !== prevDerivedRoutableVlans) {
    setPrevDerivedRoutableVlans(derivedRoutableVlans);
    setRoutableVlans(derivedRoutableVlans);
  }

  // Step 3 — Vendor VLANs
  const [vendorFilter, setVendorFilter] = useState<VlanFilterConfig>({
    network_mask: "",
    version: "",
    type: "",
  });

  const derivedVendorVlans = useMemo(() => {
    if (allVlans.length === 0 || !vendorFilter.network_mask) return [];
    const filtered = allVlans.filter((v) => {
      const versionKeys = v.version.map((ver) => `${ver.year}v${ver.version}`);
      return (
        !v.routable &&
        v.netmask.includes(vendorFilter.network_mask) &&
        versionKeys.includes(vendorFilter.version)
      );
    });
    const hotelSubnet = isValidIpv4(general.hotel_subnet) ? general.hotel_subnet : "10.0.0.0";
    return postProcessVlans(filtered, hotelSubnet, false);
  }, [allVlans, vendorFilter, general.hotel_subnet]);

  const [vendorVlans, setVendorVlans] = useState(derivedVendorVlans);
  const [prevDerivedVendorVlans, setPrevDerivedVendorVlans] = useState(derivedVendorVlans);
  if (derivedVendorVlans !== prevDerivedVendorVlans) {
    setPrevDerivedVendorVlans(derivedVendorVlans);
    setVendorVlans(derivedVendorVlans);
  }

  // Derive unique filter options from the raw VLAN data
  const filterOptions = useMemo(() => {
    const netmasks = [...new Set(allVlans.flatMap((v) => v.netmask))].sort();
    const versions = [
      ...new Map(
        allVlans.flatMap((v) => v.version.map((ver) => [`${ver.year}v${ver.version}`, ver]))
      ).values(),
    ].sort((a, b) => Number(b.year) - Number(a.year));
    const types = [
      ...new Map(
        allVlans.flatMap((v) => v.type.map((t) => [t.type, t.type]))
      ).values(),
    ].sort();
    return { netmasks, versions, types };
  }, [allVlans]);

  // When VLAN data first loads, default the filter selects to sensible values.
  // Use "Full" as the default type since it covers the most VLANs; fall back
  // to the first available type if "Full" isn't present in the dataset.
  useEffect(() => {
    if (allVlans.length === 0) return;
    const defaultVersion = filterOptions.versions[0]
      ? `${filterOptions.versions[0].year}v${filterOptions.versions[0].version}`
      : "";
    const defaultType =
      filterOptions.types.find((t) => t === "Full") ?? filterOptions.types[0] ?? "";
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRoutableFilter((prev) => ({
      network_mask: prev.network_mask || filterOptions.netmasks[0] || "",
      version: prev.version || defaultVersion,
      type: prev.type || defaultType,
    }));
    setVendorFilter((prev) => ({
      ...prev,
      network_mask: prev.network_mask || filterOptions.netmasks[0] || "",
      version: prev.version || defaultVersion,
    }));
  }, [allVlans, filterOptions]);

  // Step 4 — Additional Options
  const [options, setOptions] = useState<AdditionalOptions>({
    multi_circuit: false,
    clear_alerts: false,
  });

  // Modals
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fullConfigOpen, setFullConfigOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);

  // Save form
  const [saveForm, setSaveForm] = useState<SaveForm>({
    name: "",
    description: "",
    system_preset: false,
    public_config: false,
  });

  const [previewContent, setPreviewContent] = useState("");

  function buildFirewallVars(): FirewallConfigVars {
    return {
      MARSHA: general.marsha,
      HOTEL_SUBNET: general.hotel_subnet,
      PUBLIC_IP: general.public_ip,
      PUBLIC_NETWORK_MASK: general.public_netmask,
      GATEWAY: general.gateway,
      SYSTEM_TIME_ZONE: general.timezone,
      NETWORK_MASK: routableFilter.network_mask,
      type: routableFilter.type,
      ROUTABLE_VLANS: routableVlans.flatMap((v) => (v.selected ? [toTemplateVlan(v)] : [])),
      VENDOR_VLANS: vendorVlans.flatMap((v) => (v.selected ? [toTemplateVlan(v)] : [])),
      MULTI_CIRCUIT: options.multi_circuit,
      CLEAR_ALERTS: options.clear_alerts,
    };
  }

  function goToStep(next: number) {
    setStep(next);
    if ((next === 2 || next === 3) && allVlans.length === 0) {
      void loadVlans();
    }
  }

  function openStartupConfig() {
    const rendered = renderStartupTemplate(STARTUP_TEMPLATE, {
      HOTEL_SUBNET: general.hotel_subnet,
      PUBLIC_IP: general.public_ip,
      PUBLIC_NETMASK: general.public_netmask,
      GATEWAY: general.gateway,
      MARSHA: general.marsha,
      VENDOR: general.vendor,
    });
    setPreviewContent(rendered);
    setPreviewOpen(true);
  }

  function openFullConfig() {
    setPreviewContent(renderFirewallConfig(buildFirewallVars()));
    setFullConfigOpen(true);
  }

  function openPreview() {
    const vars = buildFirewallVars();
    const content =
      step === 2 ? renderRoutableVlansConfig(vars) :
      step === 3 ? renderVendorVlansConfig(vars) :
      renderFirewallConfig(vars);
    setPreviewContent(content);
    setPreviewOpen(true);
  }

  return {
    step, setStep, goToStep, general, setGeneral,
    publicIpTouched, setPublicIpTouched,
    netmaskTouched, setNetmaskTouched,
    gatewayTouched, setGatewayTouched,
    hotelSubnetTouched, setHotelSubnetTouched,
    allVlans, vlansStatus,
    routableFilter, setRoutableFilter,
    routableVlans, setRoutableVlans,
    vendorFilter, setVendorFilter,
    vendorVlans, setVendorVlans,
    filterOptions, options, setOptions,
    previewOpen, setPreviewOpen,
    fullConfigOpen, setFullConfigOpen,
    saveOpen, setSaveOpen,
    loadOpen, setLoadOpen,
    saveForm, setSaveForm,
    previewContent,
    openStartupConfig, openFullConfig, openPreview, copyText, downloadText,
  };
}

type ConfigGeneratorState = ReturnType<typeof useConfigGenerator>;

function ConfigStep1({ p }: { p: ConfigGeneratorState }) {
  return (
    <div className="flex flex-col gap-5">
      <CardTitle>General Firewall Configuration</CardTitle>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Vendor" hint="The brand of firewall you are configuring.">
          <select value={p.general.vendor}
            onChange={(e) => p.setGeneral((prev) => ({ ...prev, vendor: e.target.value }))}
            className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50">
            <option value="juniper">Juniper</option>
          </select>
        </Field>
        <Field label="MARSHA">
          <Input value={p.general.marsha} maxLength={7}
            onChange={(e) => p.setGeneral((prev) => ({ ...prev, marsha: e.target.value }))} />
        </Field>
        <Field label="Public IP"
          error={p.publicIpTouched && !isValidIpv4(p.general.public_ip) ? "Enter a valid IPv4 address (e.g. 12.29.141.9)" : undefined}>
          <IpInput value={p.general.public_ip} placeholder="e.g. 12.29.141.9"
            onChange={(v) => p.setGeneral((prev) => ({ ...prev, public_ip: v }))}
            onBlur={() => p.setPublicIpTouched(true)} />
        </Field>
        <Field label="Public Netmask"
          error={p.netmaskTouched && !isValidIpv4(p.general.public_netmask) ? "Enter a valid subnet mask (e.g. 255.255.255.224)" : undefined}>
          <IpInput value={p.general.public_netmask} placeholder="e.g. 255.255.255.224"
            onChange={(v) => p.setGeneral((prev) => ({ ...prev, public_netmask: v }))}
            onBlur={() => p.setNetmaskTouched(true)} />
        </Field>
        <Field label="Public Gateway"
          error={p.gatewayTouched && !isValidIpv4(p.general.gateway) ? "Enter a valid IPv4 address (e.g. 12.29.141.1)" : undefined}>
          <IpInput value={p.general.gateway} placeholder="e.g. 12.29.141.1"
            onChange={(v) => p.setGeneral((prev) => ({ ...prev, gateway: v }))}
            onBlur={() => p.setGatewayTouched(true)} />
        </Field>
        <Field label="Hotel Subnet" hint="The private IP subnet for this hotel."
          error={p.hotelSubnetTouched && !isValidIpv4(p.general.hotel_subnet) ? "Enter a valid IPv4 address (e.g. 172.16.0.0)" : undefined}>
          <IpInput value={p.general.hotel_subnet} placeholder="e.g. 172.16.0.0"
            onChange={(v) => p.setGeneral((prev) => ({ ...prev, hotel_subnet: v }))}
            onBlur={() => p.setHotelSubnetTouched(true)} />
        </Field>
        <Field label="Timezone" className="sm:col-span-2">
          <select value={p.general.timezone}
            onChange={(e) => p.setGeneral((prev) => ({ ...prev, timezone: e.target.value }))}
            className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50">
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </Field>
      </div>
      <div className="flex flex-wrap gap-2 border-t border-line pt-4">
        <Button variant="ghost" size="sm" onClick={() => p.setLoadOpen(true)}>
          <FolderOpen size={14} />Load Config
        </Button>
        <Button variant="ghost" size="sm" onClick={p.openStartupConfig}>
          <Eye size={14} />Start Up Config
        </Button>
        <Button className="ml-auto" size="sm" onClick={() => p.goToStep(2)}>
          Next Step<ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}

function ConfigStep2({ p }: { p: ConfigGeneratorState }) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <CardTitle>Routable VLANs</CardTitle>
        <CardSub className="mt-0.5">
          {p.routableVlans.length} VLANs · filtered from {p.allVlans.filter((v) => v.routable).length} routable
        </CardSub>
      </div>

      {p.vlansStatus === "loading" ? (
        <FormSkeleton lines={4} />
      ) : p.vlansStatus === "error" ? (
        <div className="rounded-xl border border-rose/40 bg-rose/10 px-4 py-3 text-sm text-rose">
          Failed to load VLAN data. Check your connection and try refreshing the page.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <Field label="GPNS Subnet Length">
              <select
                value={p.routableFilter.network_mask}
                onChange={(e) => p.setRoutableFilter((prev) => ({ ...prev, network_mask: e.target.value }))}
                className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              >
                {p.filterOptions.netmasks.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Field>

            <Field label="Version">
              <select
                value={p.routableFilter.version}
                onChange={(e) => p.setRoutableFilter((prev) => ({ ...prev, version: e.target.value }))}
                className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              >
                {p.filterOptions.versions.map((v) => {
                  const key = `${v.year}v${v.version}`;
                  return <option key={key} value={key}>{key}</option>;
                })}
              </select>
            </Field>

            <Field label="GPNS Type">
              <select
                value={p.routableFilter.type}
                onChange={(e) => p.setRoutableFilter((prev) => ({ ...prev, type: e.target.value }))}
                className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              >
                {p.filterOptions.types.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
          </div>

          <RoutableVlanTable vlans={p.routableVlans} onChange={p.setRoutableVlans} />
        </>
      )}

      <div className="flex flex-wrap gap-2 border-t border-line pt-4">
        <Button variant="ghost" size="sm" onClick={() => p.setStep(1)}>
          <ChevronLeft size={14} />
          Previous Step
        </Button>
        <Button variant="ghost" size="sm" onClick={p.openPreview}>
          <Eye size={14} />
          Preview
        </Button>
        <Button className="ml-auto" size="sm" onClick={() => p.goToStep(3)}>
          Next Step
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}

function ConfigStep3({ p }: { p: ConfigGeneratorState }) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <CardTitle>Vendor VLANs</CardTitle>
        <CardSub className="mt-0.5">
          {p.vendorVlans.length} VLANs · filtered from {p.allVlans.filter((v) => !v.routable).length} non-routable
        </CardSub>
      </div>

      {p.vlansStatus === "loading" ? (
        <FormSkeleton lines={4} />
      ) : p.vlansStatus === "error" ? (
        <div className="rounded-xl border border-rose/40 bg-rose/10 px-4 py-3 text-sm text-rose">
          Failed to load VLAN data. Check your connection and try refreshing the page.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <Field label="GPNS Subnet Length">
              <select
                value={p.vendorFilter.network_mask}
                onChange={(e) => p.setVendorFilter((prev) => ({ ...prev, network_mask: e.target.value }))}
                className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              >
                {p.filterOptions.netmasks.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Field>

            <Field label="Version">
              <select
                value={p.vendorFilter.version}
                onChange={(e) => p.setVendorFilter((prev) => ({ ...prev, version: e.target.value }))}
                className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              >
                {p.filterOptions.versions.map((v) => {
                  const key = `${v.year}v${v.version}`;
                  return <option key={key} value={key}>{key}</option>;
                })}
              </select>
            </Field>
          </div>

          <RoutableVlanTable vlans={p.vendorVlans} onChange={p.setVendorVlans} scrollable />
        </>
      )}

      <div className="flex flex-wrap gap-2 border-t border-line pt-4">
        <Button variant="ghost" size="sm" onClick={() => p.setStep(2)}>
          <ChevronLeft size={14} />
          Previous Step
        </Button>
        <Button variant="ghost" size="sm" onClick={p.openPreview}>
          <Eye size={14} />
          VLANs Preview
        </Button>
        <Button className="ml-auto" size="sm" onClick={() => p.setStep(4)}>
          Next Step
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}

function ConfigStep4({ p }: { p: ConfigGeneratorState }) {
  return (
    <div className="flex flex-col gap-5">
      <CardTitle>Additional Options</CardTitle>
      <div className="flex flex-col gap-3">
        <label className="flex items-center gap-3 rounded-xl border border-line bg-surface p-3.5 text-sm">
          <input type="checkbox" checked={p.options.multi_circuit}
            onChange={(e) => p.setOptions((prev) => ({ ...prev, multi_circuit: e.target.checked }))}
            className="accent-violet" />
          <span className="font-medium text-fg">Multi-Circuit Config</span>
        </label>
        <label className="flex items-center gap-3 rounded-xl border border-line bg-surface p-3.5 text-sm">
          <input type="checkbox" checked={p.options.clear_alerts}
            onChange={(e) => p.setOptions((prev) => ({ ...prev, clear_alerts: e.target.checked }))}
            className="accent-violet" />
          <span className="font-medium text-fg">Clear Alerts</span>
        </label>
      </div>
      <div className="flex flex-wrap gap-2 border-t border-line pt-4">
        <Button variant="ghost" size="sm" onClick={() => p.setStep(3)}>
          <ChevronLeft size={14} />Previous Step
        </Button>
        <Button variant="ghost" size="sm" onClick={p.openPreview}>
          <Eye size={14} />Preview
        </Button>
        <Button variant="ghost" size="sm" onClick={p.openFullConfig}>
          <FileText size={14} />Full Configuration
        </Button>
        <Button size="sm" onClick={() => p.setSaveOpen(true)}>
          <Save size={14} />Save Config
        </Button>
      </div>
    </div>
  );
}

export function ConfigurationGeneratorPage() {
  const p = useConfigGenerator();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">Operations</Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Configuration Generator</span>
      </div>

      <div>
        <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">Configuration Generator</h1>
        <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
          Step through the firewall configuration wizard to generate a deployable config file.
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-6">
          <div className="flex justify-center pt-2">
            <StepIndicator current={p.step} />
          </div>
          {p.step === 1 && <ConfigStep1 p={p} />}
          {p.step === 2 && <ConfigStep2 p={p} />}
          {p.step === 3 && <ConfigStep3 p={p} />}
          {p.step === 4 && <ConfigStep4 p={p} />}
        </CardContent>
      </Card>

      <Modal title="Preview" open={p.previewOpen} onClose={() => p.setPreviewOpen(false)}
        footer={<>
          <Button variant="ghost" size="sm"
            onClick={() => p.downloadText(p.previewContent, `${p.general.marsha || "config"}-preview.txt`)}>
            <Download size={13} />Download
          </Button>
          <Button size="sm" onClick={() => p.copyText(p.previewContent)}>
            <ClipboardCopy size={13} />Copy
          </Button>
        </>}>
        <pre className="min-h-[200px] rounded-xl bg-bg-0 p-4 font-mono text-[12px] leading-relaxed text-fg">
          {p.previewContent}
        </pre>
      </Modal>

      <Modal title="Full Configuration" open={p.fullConfigOpen} onClose={() => p.setFullConfigOpen(false)}
        footer={<>
          <Button variant="ghost" size="sm"
            onClick={() => p.downloadText(p.previewContent, `${p.general.marsha || "config"}-full.txt`)}>
            <Download size={13} />Download
          </Button>
          <Button size="sm" onClick={() => p.copyText(p.previewContent)}>
            <ClipboardCopy size={13} />Copy
          </Button>
        </>}>
        <pre className="min-h-[200px] rounded-xl bg-bg-0 p-4 font-mono text-[12px] leading-relaxed text-fg">
          {p.previewContent}
        </pre>
      </Modal>

      <Modal title="Save Configuration" open={p.saveOpen} onClose={() => p.setSaveOpen(false)}
        footer={<Button size="sm" onClick={() => { toast.success("Configuration saved"); p.setSaveOpen(false); }}>
          <Save size={13} />Save Config
        </Button>}>
        <div className="flex flex-col gap-4">
          <Field label="Name">
            <Input value={p.saveForm.name} onChange={(e) => p.setSaveForm((prev) => ({ ...prev, name: e.target.value }))} />
          </Field>
          <Field label="Description">
            <textarea aria-label="Description" value={p.saveForm.description}
              onChange={(e) => p.setSaveForm((prev) => ({ ...prev, description: e.target.value }))}
              rows={3} className="rounded-xl border border-line bg-surface px-3 py-2 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50" />
          </Field>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-3 rounded-xl border border-line bg-surface p-3 text-sm">
              <input type="checkbox" checked={p.saveForm.system_preset}
                onChange={(e) => p.setSaveForm((prev) => ({ ...prev, system_preset: e.target.checked }))}
                className="accent-violet" />
              <span className="font-medium text-fg">System Preset</span>
            </label>
            <label className="flex items-center gap-3 rounded-xl border border-line bg-surface p-3 text-sm">
              <input type="checkbox" checked={p.saveForm.public_config}
                onChange={(e) => p.setSaveForm((prev) => ({ ...prev, public_config: e.target.checked }))}
                className="accent-violet" />
              <span className="font-medium text-fg">Public Config</span>
            </label>
          </div>
        </div>
      </Modal>

      <Modal title="Load Configuration" open={p.loadOpen} onClose={() => p.setLoadOpen(false)}
        footer={<Button size="sm" onClick={() => { toast.success("Configuration loaded"); p.setLoadOpen(false); }}>
          <FolderOpen size={13} />Load Config
        </Button>}>
        <Field label="Stored Configuration Data">
          <select className="h-10 rounded-xl border border-line bg-surface px-3 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50">
            <option value="">Select a Configuration to load</option>
          </select>
        </Field>
      </Modal>
    </div>
  );
}
